import { LazyMotion, m } from 'motion/react';
import { domAnimation } from '@/lib/motionFeatures';
import { ReactNode } from 'react';
import errorImage from '@/assets/images/http-error.svg';
import { tw } from '@/lib/tailwind';

type ErrorStatus = 400 | 401 | 403 | 500 | 502 | 503 | 504;

const defaultErrorMessages: Record<ErrorStatus, string> = {
  400: 'The request was invalid. Please check your input and try again.',
  401: 'You are not authorized to access this resource. Please sign in.',
  403: 'You do not have permission to access this resource.',
  500: 'Our server encountered an unexpected issue. Please try again later.',
  502: 'We are unable to reach our servers. Please try again later.',
  503: 'Our service is temporarily unavailable. Please try again later.',
  504: 'The request timed out. Please try again.',
};

interface ErrorPageProps {
  status: number;
  message?: string;
  children?: ReactNode;
  role?: 'alert' | 'status';
}

const getErrorMessage = (status: number, message?: string): string => {
  if (message) return message;
  return defaultErrorMessages[status as ErrorStatus] || 'An unexpected error occurred.';
};

export default function ErrorPage({ status, message, children, role = 'alert' }: ErrorPageProps) {
  return (
    <LazyMotion features={domAnimation}>
      <section className={tw('flex h-screen w-full items-center justify-center')}>
        <m.div
          className="flex flex-col items-center gap-2.5"
          role={role}
          aria-live={role === 'alert' ? 'assertive' : 'polite'}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        >
          <m.img
            src={errorImage}
            alt="HTTP Error"
            role="presentation"
            className="mb-4"
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          />
          <div className="text-center">
            <h1 className="text-2xl leading-9 font-normal text-gray-900">Error {status}</h1>
            <div
              className={tw('text-center text-sm leading-5 font-normal text-gray-600')}
              aria-label="Error details"
            >
              {getErrorMessage(status, message)}
            </div>
            {children && (
              <div
                className="mt-4"
                aria-label="Additional error information"
              >
                {children}
              </div>
            )}
          </div>
        </m.div>
      </section>
    </LazyMotion>
  );
}
